/**
 * The Snusify header's menu: the phone menu toggle, and the dropdown panels.
 *
 * The panels are native <details>, so they open and close without script and
 * share a name, so only one is open at a time. The script adds what details
 * leave out: a click outside, Escape, or following a link closes them, and
 * the phone menu closes its panels when it closes itself.
 *
 * Search is one of them (data-nav-click): its panel holds the search field,
 * which takes focus as it opens, and a close button. It opens only on a
 * click, and closes the phone menu, whose sheet it would cover.
 *
 * With a mouse they also open on hover: once the pointer has rested on an
 * item for a moment, or at once when another panel is already open, and
 * they close a moment after the pointer has left both the item and its
 * panel, so the gap on the way down can be crossed. A click on an item its
 * hover has just opened leaves the panel open rather than shutting what the
 * pointer asked for. The panel's images - the brands' cans - start loading
 * as the pointer arrives, so they are in by the time it opens. Not in the
 * phone menu, where the items are an accordion under the toggle.
 *
 * On the desktop bar the panels share one surface, the stage. It wipes down
 * as a panel opens and back up as the last one closes. Moving from one item
 * to another leaves it in place: it takes the new panel's height while the
 * old content slides away and the new comes in from the side of the item
 * just left. A closed details hides its panel at once, so what leaves is a
 * copy of it, inert, and removed once it has played out.
 *
 * On the phone bar the menu sheet and search's panel wipe down and back up
 * the same way, each on its own surface. The sheet is a page of its own,
 * from under the bar to the foot of the screen, with the page held still
 * under it.
 *
 * A custom element rather than a DOMContentLoaded hook so the header keeps
 * working when the theme editor re-renders the section in place.
 */
const HOVER_OPEN_MS = 90;
const HOVER_CLOSE_MS = 220;

/** The stage's wipe, and its change of height between panels. */
const STAGE_MS = 380;
/** Content leaving a panel, and content coming onto one. */
const LEAVE_MS = 200;
const ENTER_MS = 340;
/** How far content slides as one panel gives way to another. */
const SLIDE_PX = 24;
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
/** A panel's clip reaches up over the gap above it, so the hover bridge
    there (the panel's ::before) stays under the pointer while a clip runs. */
const CLIP_TOP = '-1.2rem';

class SnusifyHeader extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('[data-nav]');
    this.toggle = this.querySelector('[data-nav-toggle]');
    this.links = this.querySelector('[data-nav-links]');
    this.menus = [...this.querySelectorAll('[data-nav-menu]')];

    this.onToggle = () => this.setOpen(!this.isOpen);
    this.onLinkClick = (event) => {
      if (!event.target.closest('a')) return;
      this.closeMenus();
      if (this.isOpen) this.setOpen(false);
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const menu = this.menus.find((item) => item.open);
      if (menu) {
        menu.open = false;
        menu.querySelector('summary')?.focus();
        return;
      }
      if (this.isOpen) {
        this.setOpen(false);
        this.toggle?.focus();
      }
    };
    this.onDocumentClick = (event) => {
      if (this.nav?.contains(event.target)) return;
      this.closeMenus();
      if (this.isOpen) this.setOpen(false);
    };
    // Where the browser does not yet honour details[name], one open panel
    // still closes the others.
    this.onMenuToggle = (event) => {
      if (!event.target.open) {
        delete event.target.dataset.hoverOpened;
        return;
      }
      for (const menu of this.menus) {
        if (menu !== event.target) menu.open = false;
      }
      if ('navClick' in event.target.dataset) {
        if (this.isOpen) this.setOpen(false, { keepMenus: true });
        event.target.querySelector('[data-search-field]')?.focus({ preventScroll: true });
      }
    };

    this.hoverTimer = 0;
    this.hoverable = window.matchMedia('(hover: hover)');
    this.onMenuEnter = (event) => {
      if (!this.canHover(event) || 'navClick' in event.currentTarget.dataset) return;
      const menu = event.currentTarget;
      clearTimeout(this.hoverTimer);
      if (menu.open) return;
      for (const img of menu.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
      const another = this.menus.some((item) => item.open);
      this.hoverTimer = window.setTimeout(
        () => {
          menu.open = true;
          menu.dataset.hoverOpened = '';
        },
        another ? 0 : HOVER_OPEN_MS
      );
    };
    this.onMenuLeave = (event) => {
      if (!this.canHover(event) || 'navClick' in event.currentTarget.dataset) return;
      const menu = event.currentTarget;
      clearTimeout(this.hoverTimer);
      if (!menu.open) return;
      this.hoverTimer = window.setTimeout(() => {
        menu.open = false;
      }, HOVER_CLOSE_MS);
    };
    this.onSummaryClick = (event) => {
      clearTimeout(this.hoverTimer);
      const menu = event.currentTarget.parentElement;
      if (!(menu instanceof HTMLDetailsElement) || !menu.open || !('hoverOpened' in menu.dataset)) return;
      event.preventDefault();
      delete menu.dataset.hoverOpened;
    };

    this.stage = this.querySelector('[data-menu-stage]');
    this.current = null;
    this.wide = window.matchMedia('(min-width: 901px)');
    this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    /** @type {WeakMap<Element, Animation>} */
    this.entering = new WeakMap();
    if (this.stage && this.nav && this.links) {
      this.nav.dataset.menuStaged = '';
      // An observer, not the toggle event: it reports before the next paint,
      // and hands over a panel closing and another opening together.
      this.stageObserver = new MutationObserver(() => this.#syncStage());
      // The whole bar, so search (in the actions bar) shares the stage too.
      this.stageObserver.observe(this.nav, { attributes: true, attributeFilter: ['open'], subtree: true });
      this.panelObserver = new ResizeObserver(() => this.#fitStage());
      this.onWideChange = () => this.#resetStage();
      this.wide.addEventListener('change', this.onWideChange);
    }

    this.toggle?.addEventListener('click', this.onToggle);
    // Search's own way out, back to the magnifier that opened it.
    for (const button of this.querySelectorAll('[data-search-close]')) {
      button.addEventListener('click', () => {
        const menu = button.closest('details');
        if (!menu) return;
        menu.open = false;
        menu.querySelector('summary')?.focus();
      });
    }
    this.links?.addEventListener('click', this.onLinkClick);
    for (const menu of this.menus) {
      menu.addEventListener('toggle', this.onMenuToggle);
      menu.addEventListener('pointerenter', this.onMenuEnter);
      menu.addEventListener('pointerleave', this.onMenuLeave);
      menu.querySelector('summary')?.addEventListener('click', this.onSummaryClick);
    }
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    document.documentElement.classList.remove('snusify-sheet-open');
    clearTimeout(this.hoverTimer);
    this.stageObserver?.disconnect();
    this.panelObserver?.disconnect();
    if (this.onWideChange) this.wide.removeEventListener('change', this.onWideChange);
  }

  /**
   * A mouse over the desktop bar: not a touch, and not the phone menu, which
   * shows its toggle.
   * @param {PointerEvent} event
   */
  canHover(event) {
    if (event.pointerType !== 'mouse' || !this.hoverable.matches) return false;
    return !this.toggle || getComputedStyle(this.toggle).display === 'none';
  }

  get isOpen() {
    return this.nav?.classList.contains('is-open') ?? false;
  }

  closeMenus() {
    for (const menu of this.menus) menu.open = false;
  }

  /**
   * @param {boolean} open
   * @param {{ keepMenus?: boolean }} [options] - leave the panels as they are
   */
  setOpen(open, options = {}) {
    if (!this.nav || !this.toggle) return;
    const was = this.isOpen;
    this.nav.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.setAttribute('aria-label', this.toggle.dataset[open ? 'labelClose' : 'labelOpen']);
    if (!open && !options.keepMenus) this.closeMenus();
    if (open !== was) this.#sheet(open);
  }

  /**
   * The phone menu's sheet: it starts under the bar, wherever the bar has
   * scrolled to, and reaches the foot of the screen. It wipes down as it
   * opens and back up once closed, held on screen by .is-closing until it has
   * gone, and the page under it stays still while it is open.
   * @param {boolean} open
   */
  #sheet(open) {
    // Before anything else, so a sheet closed on a screen that has since
    // turned wide never leaves the page held still.
    document.documentElement.classList.toggle('snusify-sheet-open', open);
    const sheet = this.links;
    if (!sheet || this.wide.matches) return;
    for (const animation of this.sheetAnimations ?? []) animation.cancel();
    this.sheetAnimations = [];

    if (open) {
      this.nav.classList.remove('is-closing');
      this.nav.style.setProperty('--snusify-sheet-top', `${sheet.getBoundingClientRect().top}px`);
      if (!this.calm.matches) this.sheetAnimations = this.#wipeIn(sheet);
      return;
    }

    if (this.calm.matches) return;
    this.nav.classList.add('is-closing');
    const animations = this.#wipeOut(sheet);
    this.sheetAnimations = animations;
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      // Reopened in the meantime: that opening has taken over.
      if (this.sheetAnimations !== animations) return;
      this.nav.classList.remove('is-closing');
      // Hidden now, so dropping the held end state shows nothing, and the
      // bar comes back whole should the screen turn wide.
      for (const animation of animations) animation.cancel();
      this.sheetAnimations = [];
    });
  }

  /**
   * On the phone bar the other menus are the sheet's accordion, but search
   * still drops a panel of its own below the bar, and it opens and closes
   * as the stage does on the desktop.
   * @param {HTMLDetailsElement | null} prev
   * @param {HTMLDetailsElement | null} next
   */
  #syncDrop(prev, next) {
    if (this.calm.matches) return;
    if (prev && !prev.open && 'navClick' in prev.dataset) this.#dropOut(prev);
    const panel = next && 'navClick' in next.dataset ? this.#panel(next) : null;
    if (panel) this.#wipeIn(panel);
  }

  /**
   * Plays a closed drop panel out as a copy of it, since a closed details
   * hides its panel at once. The copy sits in a holder that carries the
   * menu's classes, so the panel's own rules still reach it, and that lays
   * out nothing itself.
   * @param {HTMLDetailsElement} menu
   */
  #dropOut(menu) {
    const panel = this.#panel(menu);
    if (!panel || !menu.parentElement) return;

    const holder = document.createElement('div');
    holder.className = menu.className;
    holder.style.display = 'contents';
    holder.dataset.ghostHolder = '';
    holder.setAttribute('aria-hidden', 'true');
    holder.inert = true;

    const ghost = /** @type {HTMLElement} */ (panel.cloneNode(true));
    ghost.classList.add('snusify-menu__panel--ghost');
    for (const el of ghost.querySelectorAll('[id]')) el.removeAttribute('id');
    // What was typed goes with it.
    const fields = panel.querySelectorAll('input');
    ghost.querySelectorAll('input').forEach((field, i) => {
      field.value = fields[i]?.value ?? '';
    });

    holder.append(ghost);
    menu.parentElement.append(holder);
    Promise.allSettled(this.#wipeOut(ghost).map((animation) => animation.finished)).then(() => holder.remove());
  }

  /**
   * A surface wiping down onto the screen, its content fading in as it is
   * uncovered: the desktop stage's first opening.
   * @param {HTMLElement} surface
   * @returns {Animation[]}
   */
  #wipeIn(surface) {
    return [
      surface.animate({ clipPath: ['inset(0 0 100% 0)', 'inset(0)'] }, { duration: STAGE_MS, easing: EASE }),
      ...[...surface.children].map((child) =>
        child.animate({ opacity: [0, 1] }, { duration: ENTER_MS, delay: 40, easing: EASE, fill: 'backwards' })
      ),
    ];
  }

  /**
   * And back up, its content fading as it goes, held at the end until the
   * caller takes it away.
   * @param {HTMLElement} surface
   * @returns {Animation[]}
   */
  #wipeOut(surface) {
    return [
      surface.animate(
        { clipPath: ['inset(0)', 'inset(0 0 100% 0)'] },
        { duration: STAGE_MS, easing: EASE, fill: 'forwards' }
      ),
      ...[...surface.children].map((child) =>
        child.animate({ opacity: [1, 0] }, { duration: STAGE_MS, easing: 'ease-in', fill: 'forwards' })
      ),
    ];
  }

  /**
   * Brings the stage in line with the menus once one opens or closes: wipes
   * it down onto a panel, moves it from one panel to the next, or wipes it
   * back up.
   */
  #syncStage() {
    const next =
      this.menus.find((menu) => menu.open && menu !== this.current) ?? this.menus.find((menu) => menu.open) ?? null;
    const prev = this.current;
    if (next === prev) return;
    this.current = next;
    if (!this.wide.matches) {
      this.#syncDrop(prev, next);
      return;
    }

    const panel = next ? this.#panel(next) : null;
    const from = this.stage.classList.contains('is-shown') ? this.stage.getBoundingClientRect().height : 0;
    const to = panel ? panel.offsetHeight : 0;

    this.panelObserver.disconnect();
    if (panel) this.panelObserver.observe(panel);

    this.stageAnimation?.cancel();
    this.#setStage(to);
    if (this.calm.matches) return;

    const dir = prev && next ? this.#direction(prev, next) : 0;
    const timing = { duration: STAGE_MS, easing: EASE };
    if (prev) this.#leave(prev, from, to, dir);

    // Held open for the way up; hidden once it is folded away.
    if (to === 0) this.stage.classList.add('is-shown');
    this.stageAnimation = this.stage.animate({ blockSize: [`${from}px`, `${to}px`] }, timing);
    this.stageAnimation.finished.then(
      () => {
        if (!this.current) this.stage.classList.remove('is-shown');
      },
      () => {}
    );

    if (!panel) return;
    // Cut to the stage while the stage opens out under it.
    if (to > from) {
      panel.animate({ clipPath: [`inset(${CLIP_TOP} 0 ${to - from}px 0)`, `inset(${CLIP_TOP} 0 0 0)`] }, timing);
    }
    // In from the side of the item just left, or, on a first opening, in
    // place as the wipe uncovers it.
    const offset = `${-dir * SLIDE_PX}px 0`;
    for (const child of panel.children) {
      const enter = child.animate(
        { opacity: [0, 1], translate: [offset, '0 0'] },
        { duration: ENTER_MS, delay: dir ? 60 : 40, easing: EASE, fill: 'backwards' }
      );
      this.entering.set(panel, enter);
    }
  }

  /**
   * Plays a closed panel out as a copy of it: sliding away from the item now
   * open, or wiped up with the stage when none is. The copy is cut to the
   * stage as the stage changes height.
   * @param {HTMLDetailsElement} menu
   * @param {number} from - the stage's height as the change starts
   * @param {number} to - its height once done
   * @param {number} dir - 1 when the item now open is right of this one, -1 left, 0 none
   */
  #leave(menu, from, to, dir) {
    const panel = this.#panel(menu);
    if (!panel || !menu.parentElement) return;

    const ghost = /** @type {HTMLElement} */ (panel.cloneNode(true));
    ghost.classList.add('snusify-menu__panel--ghost');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.inert = true;
    for (const el of ghost.querySelectorAll('[id]')) el.removeAttribute('id');
    // Beside the details, so the copy is styled as the panel was.
    menu.parentElement.append(ghost);

    // A panel still coming in leaves from as far as it had got.
    const shown = this.entering.get(panel)?.effect?.getComputedTiming().progress ?? 1;
    const height = ghost.offsetHeight;
    const cut = (visible) => `inset(${CLIP_TOP} 0 ${Math.max(height - visible, 0)}px 0)`;

    const animations = [
      ghost.animate({ clipPath: [cut(from), cut(to)] }, { duration: STAGE_MS, easing: EASE, fill: 'forwards' }),
      ghost.animate(
        { opacity: [shown, 0], translate: ['0 0', `${dir * SLIDE_PX}px 0`] },
        { duration: dir ? LEAVE_MS : STAGE_MS, easing: 'ease-in', fill: 'forwards' }
      ),
    ];
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => ghost.remove());
  }

  /** Keeps the stage the open panel's height when the panel itself changes, as its cans load or a font arrives. */
  #fitStage() {
    if (!this.current || !this.wide.matches) return;
    const panel = this.#panel(this.current);
    if (panel) this.stage.style.blockSize = `${panel.offsetHeight}px`;
  }

  /** Starts over when the bar turns into the phone menu or back: no copies, no stage under the accordion. */
  #resetStage() {
    this.stageAnimation?.cancel();
    for (const ghost of this.querySelectorAll('.snusify-menu__panel--ghost, [data-ghost-holder]')) ghost.remove();
    this.current = this.menus.find((menu) => menu.open) ?? null;
    const panel = this.current && this.wide.matches ? this.#panel(this.current) : null;
    this.#setStage(panel ? panel.offsetHeight : 0);
  }

  /**
   * @param {number} height
   */
  #setStage(height) {
    this.stage.style.blockSize = `${height}px`;
    this.stage.classList.toggle('is-shown', height > 0);
  }

  /**
   * @param {HTMLDetailsElement} menu
   * @returns {HTMLElement | null}
   */
  #panel(menu) {
    return menu.querySelector(':scope > .snusify-menu__panel');
  }

  /**
   * Which way the pointer went: 1 when the item now open is right of the one
   * it left, -1 when left of it. Read off the page, so it holds right to left.
   * @param {Element} prev
   * @param {Element} next
   * @returns {number}
   */
  #direction(prev, next) {
    return next.getBoundingClientRect().left >= prev.getBoundingClientRect().left ? 1 : -1;
  }
}

if (!customElements.get('snusify-header')) {
  customElements.define('snusify-header', SnusifyHeader);
}
